import { useState, useEffect, forwardRef } from 'react';
import { useMediaQuery } from '@mui/material';
import { Theme } from '../../types/enums';
import { PaginationVersion } from '../types/enums';
import Pagination from '../Pagination/Pagination';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { PaginationDataInterface, TableProps } from '../types/propType';
import { TableBody } from '../TableComponents';
import styles from './DashboardTable.module.scss';

const DashboardTable = forwardRef<HTMLTableElement, TableProps>(
  ({
    columns,
    dataSource,
    rowKey,
    onRowClick,
    selected,
    setSelectedKeys,
    className,
    pagination = false,
    headerTheme = Theme.GRAY,
    sortingCallback,
    paginationObject,
    paginationCallback,
    enableCheckbox = false,
    formatPaginationCallback,
    forwardedRef,
    disabledItems,
    hideSearchFilter = false,
    ...rest
  }: TableProps) => {
    const initialPagination = paginationObject || {
      totalRecords: dataSource.length,
      rowsPerPage: 6,
      currentPage: 0,
      recordStartIndex: 0,
      recordEndIndex: 6,
    };
    const [paginationData, setPaginationData] = useState<PaginationDataInterface>(initialPagination);
    const [tableData, setTableData] = useState<DynamicObjectInterface[]>(dataSource);

    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
      setPaginationData(initialPagination);
    }, [dataSource]);

    const getWidth = (width = 0) => {
      if (width) {
        return { width: `${width}%` };
      }

      return {};
    };

    return (
      <>
        {pagination && isMobile && !hideSearchFilter? (
          <Pagination
            formatPaginationCallback={formatPaginationCallback}
            paginationVersion={PaginationVersion.ADMIN}
            {...paginationData}
            updatePaginationData={(updatedData) => {
              paginationCallback?.({
                ...paginationData,
                ...updatedData,
              });
              setPaginationData({
                ...paginationData,
                ...updatedData,
              });
            }}
            hidePagination={true}
            fromDashboard={true}
          />
        ) : (
          ''
        )}
        <table
          className={`${styles['table-container']} ${className ? className : ''}`}
          {...rest}
          data-testid="table-data-test-id"
          ref={forwardedRef}
        >
          <TableBody
            columns={columns}
            tableData={tableData}
            setTableData={setTableData}
            selected={selected}
            rowKey={rowKey}
            setSelectedKeys={setSelectedKeys}
            onRowClick={onRowClick}
            pagination={pagination}
            paginationData={paginationData}
            getWidth={getWidth}
            styles={styles}
            isServerSideFetch={!!paginationCallback}
            enableCheckbox={enableCheckbox}
            disabledItems={disabledItems}
          />
        </table>
        {pagination && !hideSearchFilter ? (
          <Pagination
            formatPaginationCallback={formatPaginationCallback}
            paginationVersion={PaginationVersion.ADMIN}
            {...paginationData}
            updatePaginationData={(updatedData) => {
              paginationCallback?.({
                ...paginationData,
                ...updatedData,
              });
              setPaginationData({
                ...paginationData,
                ...updatedData,
              });
            }}
            isMobile={isMobile}
            fromDashboard={true}
          />
        ) : (
          ''
        )}
      </>
    );
  },
);
DashboardTable.displayName = 'DashboardTable';

export default DashboardTable;
export type { PaginationDataInterface };
